import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { ArrowRight, Search, ShieldCheck, CheckCircle2, Briefcase, UserCheck } from "lucide-react";
import Footer from "@/components/Footer";


export default function Landing({ claimCoach = null }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
    track("homepage_visit");
  }, []);

  const first = claimCoach?.name ? claimCoach.name.split(" ")[0] : null;

  return (
    <div className="min-h-screen bg-appbg text-white">
      {/* Founding coach strip — coach-first: the founders' offer for programs
          still exists, but the top banner sells the coach funnel. */}
      <Link to="/pricing" data-testid="pricing-strip" className="block bg-blaze text-white text-center px-4 py-2 text-xs tracking-widest hover:opacity-95">
        COACHES ARE FREE · JOB POSTING IS FREE · PROGRAM RECRUITING <span className="heading bg-black/30 px-2 py-0.5 mx-1">$5/MO</span> PER PROGRAM — SEE PRICING
      </Link>

      {/* HERO */}
      <section className="px-4 md:px-8 lg:px-12 pt-16 pb-14 md:pt-24 md:pb-20" data-testid="hero-section">
        <div className="max-w-5xl mx-auto">
          {first ? (
            <h1 className="heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95]" data-testid="hero-claim-personalized">
              BUILT FOR COACHES <span className="text-blaze">LIKE YOU, {first.toUpperCase()}.</span>
            </h1>
          ) : (
            <h1 className="heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95]" data-testid="hero-headline">
              THE RIGHT COACH.<br />THE RIGHT PROGRAM.<br /><span className="text-blaze">THE RIGHT FIT.</span>
            </h1>
          )}
          <p className="mt-6 text-base md:text-lg text-neutral-300 max-w-2xl leading-relaxed" data-testid="hero-subheadline">
            CoachConnect connects coaches with opportunities and helps athletic programs find coaches who actually fit.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl">
            <Link to="/coaches/find" onClick={() => track("coach_cta_click")} data-testid="hero-coach-cta"
              className="bg-blaze text-white p-6 hover:glow-blaze transition-all group">
              <div className="heading text-2xl flex items-center gap-2">I&apos;M A COACH <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" /></div>
              <div className="text-sm text-white/85 mt-1">Claim or build my free coaching profile</div>
            </Link>
            <Link to="/post-job" onClick={() => track("hiring_cta_click")} data-testid="hero-hiring-cta"
              className="border-2 border-volt text-volt p-6 hover:bg-volt hover:text-white transition-all group">
              <div className="heading text-2xl flex items-center gap-2">I&apos;M HIRING <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" /></div>
              <div className="text-sm opacity-85 mt-1">Post a coaching job — free</div>
            </Link>
          </div>
          <Link to="/jobs/weekly" onClick={() => track("job_page_visit", { source: "hero" })} data-testid="hero-browse-openings"
            className="mt-6 inline-flex items-center gap-2 border border-neutral-600 text-white heading text-sm px-5 py-3 hover:border-volt hover:text-volt transition-colors">
            BROWSE THIS WEEK&apos;S COACHING OPENINGS — NO ACCOUNT NEEDED <ArrowRight size={16} />
          </Link>
          <a href="#how-it-works" data-testid="hero-how-link" className="block mt-4 text-sm text-neutral-400 underline underline-offset-4 hover:text-volt">
            Explore how CoachConnect works
          </a>
        </div>
      </section>

      {/* PROOF BAR — live numbers */}
      <section className="border-y border-neutral-800 bg-[#0C0C0E] px-4 py-5" data-testid="proof-bar">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-x-10 gap-y-2 text-center">
          <div data-testid="proof-coaches"><span className="heading text-2xl text-blaze">{stats ? `${stats.coaches.toLocaleString()}+` : "—"}</span> <span className="text-xs tracking-widest text-neutral-400">COACHES</span></div>
          <div data-testid="proof-jobs"><span className="heading text-2xl text-volt">{stats ? `${stats.open_jobs}+` : "—"}</span> <span className="text-xs tracking-widest text-neutral-400">OPPORTUNITIES</span></div>
          {stats?.jobs_filled > 0 && (
            <div data-testid="proof-filled"><span className="heading text-2xl text-white">{stats.jobs_filled}</span> <span className="text-xs tracking-widest text-neutral-400">ROLES FILLED</span></div>
          )}
          <div className="text-xs tracking-widest text-neutral-500">GROWING EVERY DAY</div>
        </div>
      </section>

      {/* COACH FUNNEL */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-20" data-testid="coach-funnel-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs tracking-widest text-blaze">FOR COACHES</div>
          <h2 className="heading text-3xl md:text-4xl mt-2 max-w-3xl">FIND THE RIGHT OPPORTUNITY BEFORE IT FINDS SOMEONE ELSE</h2>
          <p className="mt-4 text-neutral-300 max-w-2xl text-sm md:text-base leading-relaxed">
            CoachConnect may already have a coaching profile started for you. Claim it, complete it and let
            CoachConnect identify opportunities that fit your experience, goals and career path.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/coaches/find" onClick={() => track("coach_cta_click")} data-testid="coach-claim-cta"
              className="inline-flex items-center gap-2 bg-blaze text-white heading text-lg px-7 py-3.5 hover:glow-blaze transition-all">
              <Search size={16} /> CLAIM MY PROFILE
            </Link>
            <Link to="/auth?role=coach" data-testid="coach-create-cta"
              className="inline-flex items-center gap-2 border border-neutral-600 text-neutral-200 heading text-lg px-7 py-3.5 hover:border-volt hover:text-volt transition-colors">
              CREATE MY PROFILE
            </Link>
          </div>
        </div>
      </section>

      {/* PROGRAM FUNNEL */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-20 border-t border-neutral-800 bg-[#0C0C0E]" data-testid="program-funnel-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs tracking-widest text-volt">FOR PROGRAMS</div>
          <h2 className="heading text-3xl md:text-4xl mt-2">LOOKING FOR YOUR NEXT COACH?</h2>
          <p className="mt-4 text-neutral-300 max-w-2xl text-sm md:text-base leading-relaxed">
            Tell CoachConnect what your program needs. We&apos;ll identify coaches whose experience, career goals
            and coaching profile fit the opportunity.
          </p>
          <Link to="/post-job" onClick={() => track("hiring_cta_click")} data-testid="program-post-cta"
            className="mt-7 inline-flex items-center gap-2 bg-volt text-white heading text-lg px-7 py-3.5 hover:opacity-90 transition-opacity">
            <Briefcase size={16} /> POST A COACHING JOB — FREE
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="px-4 md:px-8 lg:px-12 py-16 md:py-20 border-t border-neutral-800" data-testid="how-it-works">
        <div className="max-w-5xl mx-auto">
          <h2 className="heading text-3xl md:text-4xl">HOW IT WORKS</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-10">
            <div data-testid="how-coaches">
              <div className="text-xs tracking-widest text-blaze mb-4">COACHES</div>
              {[["Claim your profile", "Find or create your coaching profile."],
                ["Tell us what you want", "Add career goals, preferences and experience."],
                ["Discover opportunities", "CoachConnect identifies programs that fit."]].map(([t, d], i) => (
                <div key={t} className="flex gap-4 mb-5">
                  <div className="heading text-3xl text-blaze/60 shrink-0">{i + 1}</div>
                  <div><div className="heading text-lg">{t.toUpperCase()}</div><div className="text-sm text-neutral-400">{d}</div></div>
                </div>
              ))}
              <Link to="/coaches/find" onClick={() => track("coach_cta_click")} data-testid="how-coach-cta" className="inline-flex items-center gap-2 text-blaze heading text-base hover:underline underline-offset-4">CLAIM MY PROFILE <ArrowRight size={14} /></Link>
            </div>
            <div data-testid="how-programs">
              <div className="text-xs tracking-widest text-volt mb-4">PROGRAMS</div>
              {[["Post your opportunity", "Tell us what your program needs."],
                ["See matching coaches", "CoachConnect identifies potential fits."],
                ["Connect", "Evaluate candidates and begin the conversation."]].map(([t, d], i) => (
                <div key={t} className="flex gap-4 mb-5">
                  <div className="heading text-3xl text-volt/60 shrink-0">{i + 1}</div>
                  <div><div className="heading text-lg">{t.toUpperCase()}</div><div className="text-sm text-neutral-400">{d}</div></div>
                </div>
              ))}
              <Link to="/post-job" onClick={() => track("hiring_cta_click")} data-testid="how-program-cta" className="inline-flex items-center gap-2 text-volt heading text-base hover:underline underline-offset-4">POST A JOB <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ONE MATCH EXAMPLE */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-20 border-t border-neutral-800 bg-[#0C0C0E]" data-testid="match-example">
        <div className="max-w-5xl mx-auto">
          <h2 className="heading text-3xl md:text-4xl">WHY THIS MATCH WORKS</h2>
          <div className="mt-8 grid md:grid-cols-[1fr_auto_1fr] gap-6 items-stretch">
            <div className="border border-neutral-800 bg-appcard p-6" data-testid="match-program-needs">
              <div className="text-xs tracking-widest text-volt">PROGRAM NEEDS</div>
              <ul className="mt-3 space-y-2 text-sm text-neutral-300">
                {["Experienced offensive coach", "Recruiting background", "Southeast preferred", "Player-development focus"].map((x) => (
                  <li key={x} className="flex gap-2"><CheckCircle2 size={14} className="text-volt shrink-0 mt-0.5" />{x}</li>
                ))}
              </ul>
            </div>
            <div className="flex md:flex-col items-center justify-center gap-2">
              <div className="heading text-4xl text-blaze" data-testid="match-fit-score">91%</div>
              <div className="text-[10px] tracking-widest text-neutral-500 text-center">COACHCONNECT<br />FIT</div>
            </div>
            <div className="border border-neutral-800 bg-appcard p-6" data-testid="match-coach-profile">
              <div className="text-xs tracking-widest text-blaze">COACH PROFILE</div>
              <ul className="mt-3 space-y-2 text-sm text-neutral-300">
                {["10 years collegiate experience", "Offensive coordinator", "Southeast relocation preference", "Strong player-development history"].map((x) => (
                  <li key={x} className="flex gap-2"><CheckCircle2 size={14} className="text-blaze shrink-0 mt-0.5" />{x}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-6 text-neutral-400 text-sm">CoachConnect finds alignment that a traditional job board cannot.</p>
          <Link to="/coaches/find" onClick={() => track("coach_cta_click")} data-testid="match-example-cta" className="mt-4 inline-flex items-center gap-2 text-blaze heading text-base hover:underline underline-offset-4">SEE MY MATCHES <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* PRIVACY */}
      <section className="px-4 md:px-8 lg:px-12 py-16 border-t border-neutral-800" data-testid="privacy-section">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-center gap-6">
          <ShieldCheck size={40} className="text-volt shrink-0" />
          <div className="flex-1">
            <h2 className="heading text-2xl md:text-3xl">EXPLORE WITHOUT PUTTING YOUR CURRENT JOB AT RISK</h2>
            <p className="mt-2 text-neutral-300 text-sm md:text-base max-w-2xl">
              Your interest in opportunities stays private. Programs do not know you&apos;re interested unless you choose to engage.
            </p>
          </div>
          <Link to="/coaches/find" onClick={() => track("coach_cta_click")} data-testid="privacy-cta"
            className="shrink-0 inline-flex items-center gap-2 border border-volt text-volt heading text-base px-6 py-3 hover:bg-volt hover:text-white transition-colors">
            CREATE MY PRIVATE PROFILE
          </Link>
        </div>
      </section>

      {/* SIMPLE PRICING */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-20 border-t border-neutral-800 bg-[#0C0C0E]" data-testid="simple-pricing">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="border border-neutral-800 bg-appcard p-8" data-testid="pricing-coaches">
            <div className="text-xs tracking-widest text-blaze">FOR COACHES</div>
            <ul className="mt-4 space-y-2 text-sm text-neutral-300">
              <li className="flex gap-2"><CheckCircle2 size={15} className="text-blaze shrink-0 mt-0.5" />Build your profile — <b className="text-white">FREE</b></li>
              <li className="flex gap-2"><CheckCircle2 size={15} className="text-blaze shrink-0 mt-0.5" />Discover opportunities — <b className="text-white">FREE</b></li>
              <li className="flex gap-2"><CheckCircle2 size={15} className="text-blaze shrink-0 mt-0.5" />Full match details &amp; replies — <b className="text-white">FREE</b></li>
            </ul>
            <Link to="/coaches/find" onClick={() => track("coach_cta_click")} data-testid="pricing-coach-cta" className="mt-6 inline-flex items-center gap-2 bg-blaze text-white heading text-base px-6 py-3 hover:glow-blaze transition-all">BUILD MY PROFILE</Link>
          </div>
          <div className="border border-neutral-800 bg-appcard p-8" data-testid="pricing-programs">
            <div className="text-xs tracking-widest text-volt">FOR PROGRAMS</div>
            <ul className="mt-4 space-y-2 text-sm text-neutral-300">
              <li className="flex gap-2"><CheckCircle2 size={15} className="text-volt shrink-0 mt-0.5" />Post a job — <b className="text-white">FREE</b></li>
              <li className="flex gap-2"><CheckCircle2 size={15} className="text-volt shrink-0 mt-0.5" />See initial matches — <b className="text-white">FREE</b></li>
              <li className="flex gap-2"><CheckCircle2 size={15} className="text-volt shrink-0 mt-0.5" />Program Recruiting — talent search, matching, market intel — <b className="text-white">$5/month</b></li>
            </ul>
            <Link to="/post-job" onClick={() => track("hiring_cta_click")} data-testid="pricing-program-cta" className="mt-6 inline-flex items-center gap-2 bg-volt text-white heading text-base px-6 py-3 hover:opacity-90 transition-opacity">POST A JOB</Link>
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-4 text-xs text-neutral-500">
          Full plans on the <Link to="/pricing" className="underline underline-offset-2 hover:text-volt" data-testid="pricing-details-link">pricing page</Link> · How the matching works on the <Link to="/technology" className="underline underline-offset-2 hover:text-volt" data-testid="tech-page-link">technology page</Link>
        </div>
      </section>

      {/* FOUNDER — short */}
      <section className="px-4 md:px-8 lg:px-12 py-16 border-t border-neutral-800" data-testid="founder-short">
        <div className="max-w-5xl mx-auto">
          <h2 className="heading text-2xl md:text-3xl">BUILT BY COACHES. FOR COACHES.</h2>
          <p className="mt-3 text-neutral-300 text-sm md:text-base max-w-2xl leading-relaxed">
            CoachConnect was created after decades of playing, coaching and helping athletes and coaches navigate
            the recruiting and hiring process. We believe great coaching careers shouldn&apos;t depend on who happens to know whom.
          </p>
          <Link to="/who-we-are" data-testid="founder-story-cta" className="mt-4 inline-flex items-center gap-2 text-neutral-300 heading text-base hover:text-volt">OUR STORY <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 md:px-8 lg:px-12 py-20 border-t-4 border-blaze bg-[#0C0C0E]" data-testid="final-cta">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="heading text-4xl sm:text-5xl lg:text-6xl">YOUR NEXT OPPORTUNITY<br />MAY ALREADY BE HERE.</h2>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/coaches/find" onClick={() => track("coach_cta_click")} data-testid="final-coach-cta"
              className="inline-flex items-center justify-center gap-2 bg-blaze text-white heading text-xl px-10 py-5 hover:glow-blaze transition-all">
              <UserCheck size={18} /> I&apos;M A COACH — FIND MY PROFILE
            </Link>
            <Link to="/post-job" onClick={() => track("hiring_cta_click")} data-testid="final-hiring-cta"
              className="inline-flex items-center justify-center gap-2 border-2 border-volt text-volt heading text-xl px-10 py-5 hover:bg-volt hover:text-white transition-all">
              <Briefcase size={18} /> I&apos;M HIRING — POST A JOB
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Sticky mobile CTA */}
      <div className="h-14 md:hidden" aria-hidden="true" />
      <div className="fixed bottom-0 inset-x-0 z-50 md:hidden bg-blaze text-white shadow-[0_-4px_20px_rgba(0,0,0,0.6)]" data-testid="mobile-cta-bar">
        <Link to="/coaches/find" onClick={() => track("coach_cta_click")} data-testid="mobile-cta-link" className="flex items-center justify-center gap-2 py-4 heading text-lg">
          I&apos;M A COACH — FIND MY PROFILE <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
